import React, { ReactNode, createContext, useState } from 'react';

import { Box, Modal } from '@mui/material';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import MainCard from '@/components/base-component/MainCard';
import { FormattedMessage } from 'react-intl';
type AlertType = 'Info' | 'Confirm' | 'Error' | 'Warning';

type ITypeAlert = {
  type: AlertType;
  title?: string;
  message: string | React.ReactNode;
  confirmButtonColor?: string;
  hideCancelButton?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
};

type AlertContext = {
  showAlert: (data: ITypeAlert) => void;
  hideAlert: () => void;
};

type AlertContextProvider = {
  children: ReactNode;
};

// Create a new context for the Alert
export const AlertContext = createContext<AlertContext>({
  showAlert: () => {},
  hideAlert: () => {}
});

export const AlertProvider: React.FC<AlertContextProvider> = ({ children }) => {
  const [alertMessage, setAlertMessage] = useState<ITypeAlert>();
  const [open, setOpen] = useState(false);

  // Context value containing the showAlert function
  const contextValue: AlertContext = {
    showAlert: ({ type, message, confirmButtonColor, onConfirm, onCancel, title, hideCancelButton }: ITypeAlert) => {
      const am: ITypeAlert = {
        type,
        message,
        title,
        confirmButtonColor,
        hideCancelButton,
        onConfirm,
        onCancel
      };

      setAlertMessage(am);
      setOpen(true);
    },
    hideAlert: () => {
      setAlertMessage({ type: 'Info', message: '' });
      setOpen(false);
    }
  };

  const handleCloseCancel = () => {
    if (alertMessage?.onCancel) alertMessage?.onCancel();
    setOpen(false);
  };

  const handleCloseConfirm = () => {
    if (alertMessage?.onConfirm) alertMessage?.onConfirm();
    setOpen(false);
  };

  return (
    <AlertContext.Provider value={contextValue}>
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          paddingTop: 10
        }}
      >
        <Modal open={open} onClose={handleCloseCancel} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
          <MainCard title={`${alertMessage?.title ?? 'Basic Modal'}`} modal darkTitle content={false}>
            <CardContent>
              <Typography id="modal-modal-description">
                {alertMessage?.message ?? 'Duis mollis, est non commodo luctus, nisi erat porttitor ligula.'}
              </Typography>
            </CardContent>
            <Divider />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
              {!alertMessage?.hideCancelButton && (
                <Button color="error" size="small" onClick={handleCloseCancel}>
                  <FormattedMessage id="cancel" />
                </Button>
              )}

              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: alertMessage?.confirmButtonColor }}
                onClick={handleCloseConfirm}
              >
                <FormattedMessage id="enter" />
              </Button>
            </Stack>
          </MainCard>
        </Modal>
      </Box>
      {children}
    </AlertContext.Provider>
  );
};
